/**
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/PaymentAccordionItem').default>>} paymentAccordionItem
 */

/**
 * @param {ReturnType<typeof import('widgets/global/Accordion').default>} Accordion Base widget for extending
 * @returns {typeof PaymentAccordion} Payment Accordion class
 */
export default function (Accordion) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordion
     * @augments Accordion
     * @classdesc Represents PaymentAccordion components that implements logic to validate\submit payment information
     * It has next features:
     * 1. Validates current payment accordion item
     * 2. Handles current payment tab submit by processing current tab data
     *
     * @example <caption>Example of PaymentAccordion widget usage</caption>
     *    <div
     *        data-id="payment"
     *        data-widget="paymentAccordion"
     *        data-event-keydown="handleKeydown"
     *        data-allow-toggle="false"
     *        data-allow-multiple="false"
     *        data-active-panel="...panel id"
     *        data-open-first="true"
     *        data-field-name="${pdict.forms.billingForm.paymentMethod.htmlName}"
     *        class="b-payment_accordion"
     *    >
     *        <div data-ref="container">
     *            <isloop items="${pdict.order.billing.payment.applicablePaymentMethods}" var="paymentOption">
     *                <section
     *                    data-widget="paymentAccordionItem${paymentOption.ID}"
     *                    data-widget-event-closeallitems="closeItems"
     *                    data-method-id="${paymentOption.ID}"
     *                    data-method-name="${paymentOption.name}"
     *                    class="b-payment_accordion-item"
     *               >
     *                    <isinclude template="${'checkout/billing/paymentOptions/paymentTitle_' + paymentOption.ID}" />
     *                    <div
     *                        id="payment-details-${paymentOption.ID}"
     *                        data-ref="accordionItemPanel"
     *                        class="b-payment_accordion-content"
     *                    >
     *                        <div class="b-payment_accordion-content_inner data-ref="accordionItemPanelInner">
     *                            <isinclude template="${'checkout/billing/paymentOptions/paymentBody_' + paymentOption.ID}" />
     *                        </div>
     *                    </div>
     *                </section>
     *            </isloop>
     *        </div>
     *    </div>
     *
     * @property {string} data-field-name - payment field name
     */
    class PaymentAccordion extends Accordion {
        /**
         * @description Returns Widget configuration object
         * @returns {object} result
         */
        prefs() {
            return {
                fieldName: '',
                errorMessageLabel: 'errorMessageLabel',
                errorMessage: 'Please select payment method',
                ...super.prefs()
            };
        }

        /**
         * @description Validates current payment accordion item childs
         * @returns {boolean} result
         */
        validate() {
            const currentPaymentItem = this.getCurrentPaymentItem();

            if (currentPaymentItem) {
                const isValid = currentPaymentItem.isChildrenValid();

                if (!isValid && !currentPaymentItem.isPanelOpen) {
                    currentPaymentItem.openPanel();
                }

                return isValid;
            }

            return false;
        }

        /**
         * @description Handles current payment tab submit
         * @returns {Promise} result
         */
        process() {
            return new Promise((resolve, reject) => {
                const valid = this.validate();
                const currentPaymentItem = this.getCurrentPaymentItem();

                if (!currentPaymentItem || (!valid && !currentPaymentItem)) {
                    return reject(new Error(this.prefs().errorMessage));
                }

                if (!valid) {
                    return reject();
                }

                if (currentPaymentItem) {
                    const tabProcessResult = currentPaymentItem.process().then(data => {
                        return { [this.prefs().fieldName]: currentPaymentItem.prefs().methodId, ...data };
                    });

                    return resolve(tabProcessResult);
                }

                return reject();
            });
        }

        /**
         * @description Handles current payment tab error
         * @param {string} fieldName - field name with error
         * @param {string} errorMsg - error message
         * @returns {void} result
         */
        handleError(fieldName, errorMsg) {
            const currentPaymentItem = this.getCurrentPaymentItem();

            if (currentPaymentItem) {
                currentPaymentItem.handleError({
                    fieldName,
                    errorMsg
                });
            }
        }

        /**
         * @description Returns current payment accordion item
         * @returns {paymentAccordionItem|undefined} result
         */
        getCurrentPaymentItem() {
            this.eachChild(item => {
                if (item.isPanelOpen) {
                    this.currentItem = item;
                }
            });

            return this.currentItem;
        }

        /**
         * @description Clean up sensitive data handler
         * @returns {void} result
         */
        cleanupSensitiveData() {
            this.eachChild(item => {
                item.data('cleanupSensitiveData');
            });
        }
    }

    return PaymentAccordion;
}
